import { useTranslation } from '@jq4race/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@jq4race/ui';
import { AlertTriangle } from 'lucide-react';

interface SignOutConfirmDialogProps {
  pendingCount: number;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * SignOutConfirmDialog (pwa-offline-first Slice 6, task 6.9) — replaces
 * `window.confirm` for the "sign out with pending items" warning. Binding
 * requirement: iOS Safari's PWA (standalone display mode) chrome does not
 * render `window.confirm`'s native dialog consistently — an in-app dialog
 * is the only reliable cross-platform choice. Mirrors DuplicatePlateDialog's
 * own pattern (same `@jq4race/ui` Dialog primitives).
 */
export function SignOutConfirmDialog({
  pendingCount,
  onConfirm,
  onCancel,
}: SignOutConfirmDialogProps) {
  const { t } = useTranslation('location-context');

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-destructive/15">
              <AlertTriangle className="size-5 text-destructive" strokeWidth={1.5} />
            </span>
            <DialogTitle className="text-[20px]">{t('signOutConfirmTitle')}</DialogTitle>
          </div>
          <DialogDescription className="mt-1">
            {t('signOutConfirmPending', { count: pendingCount })}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button variant="ghost" size="lg" className="w-full sm:w-auto" onClick={onCancel}>
            {t('common:actions.cancel')}
          </Button>
          <Button variant="destructive" size="lg" className="w-full sm:w-auto" onClick={onConfirm}>
            {t('signOutConfirmAction')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
